import React, { useState, useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route } from 'react-router-dom';
import { PrimeReactProvider } from 'primereact/api';
import { addLocale, locale } from 'primereact/api';
import { Toast } from 'primereact/toast';
import { Menubar } from 'primereact/menubar';
import { loadTheme } from './themes';
import { ImportProvider, useImport } from './context/ImportContext';
import Dashboard from './pages/Dashboard';
import Configuration from './pages/Configuration';
import AutomatedImports from './pages/AutomatedImports';
import MissingLayoutsUploadPage from './pages/MissingLayoutsUploadPage';
import { initSession } from './Middleware/sessionAuth';
import { globalConfigAPI } from './services/globalConfigAPI';

// --------------------------------------------------------------------------
// CONFIGURAÇÃO GLOBAL DO PRIMEREACT (Executada 1 única vez na inicialização)
// --------------------------------------------------------------------------
addLocale('pt-BR', {
  firstDayOfWeek: 0,
  dayNames: ['Domingo', 'Segunda', 'Terça', 'Quarta', 'Quinta', 'Sexta', 'Sábado'],
  dayNamesShort: ['Dom', 'Seg', 'Ter', 'Qua', 'Qui', 'Sex', 'Sáb'],
  dayNamesMin: ['Do', 'Se', 'Te', 'Qu', 'Qu', 'Se', 'Sa'],
  monthNames: ['Janeiro', 'Fevereiro', 'Março', 'Abril', 'Maio', 'Junho', 'Julho', 'Agosto', 'Setembro', 'Outubro', 'Novembro', 'Dezembro'],
  monthNamesShort: ['Jan', 'Fev', 'Mar', 'Abr', 'Mai', 'Jun', 'Jul', 'Ago', 'Set', 'Out', 'Nov', 'Dez'],
  today: 'Hoje',
  clear: 'Limpar'
});

// Ativa o idioma pt-BR como padrão em todo o sistema
locale('pt-BR');

function AppContent() {
  const [currentPage, setCurrentPage] = useState('dashboard');
  const [isGlobalConfigSaved, setIsGlobalConfigSaved] = useState(false);
  const [checkingGlobalConfig, setCheckingGlobalConfig] = useState(true);
  // const [sessionUser, setSessionUser] = useState(null); // Disabled - auth via X-PHPSESSID header
  const { selectedTheme } = useImport();
  const toastRef = React.useRef(null);

  // Load theme stylesheet dynamically
  useEffect(() => {
    loadTheme(selectedTheme);
  }, [selectedTheme]);

  useEffect(() => {
    initSession(); // Logs PHPSESSID from cookie
    checkGlobalConfig();
  }, []);

  // Verifica se a configuração global já está preenchida na base do cliente
  const checkGlobalConfig = async () => {
    setCheckingGlobalConfig(true);
    try {
      const response = await globalConfigAPI.get();
      // Regra de validação: Verifique se existe o registro no banco e se os dados obrigatórios foram salvos
      if (response?.success && response?.data && response?.data?.start_day) {
        setIsGlobalConfigSaved(true);
      } else {
        setIsGlobalConfigSaved(false);
        // Redireciona para configurações se for a primeira vez / não tiver config global
        setCurrentPage('configuration');
        toastRef.current?.show({
          severity: 'warn',
          summary: 'Atenção',
          detail: 'Por favor, salve as configurações globais antes de prosseguir com o uso do sistema.',
          sticky: true
        });
      }
    } catch (error) {
      setIsGlobalConfigSaved(false);
      setCurrentPage('configuration');
    } finally {
      setCheckingGlobalConfig(false);
    }
  };

  const handlePageChange = (page) => {
    // Impede o usuário de ir para o Dashboard se a configuração global não estiver salva
    if (!isGlobalConfigSaved && page === 'dashboard') {
      toastRef.current?.show({
        severity: 'warn',
        summary: 'Ação Bloqueada',
        detail: 'É necessário salvar a Configuração Global.'
      });
      return;
    }
    setCurrentPage(page);
  };


  const menuItems = [
    {
      label: 'Dashboard',
      icon: 'pi pi-home',
      command: () => handlePageChange('dashboard')
    },
    {
      label: 'Configurações',
      icon: 'pi pi-cog',
      command: () => setCurrentPage('configuration')
    }
  ];

  if (checkingGlobalConfig) {
    return (
      <div className="flex align-items-center justify-content-center h-screen bg-gray-50">
        <i className="pi pi-spin pi-spinner text-4xl text-blue-500"></i>
      </div>
    );
  }

  return (
    <div className="h-full bg-gray-50 flex flex-col">
      <Toast ref={toastRef} />

      <div className="mx-auto w-full flex-1 min-h-0 h-full">
         <Menubar
            style={{marginLeft:'10px', marginRight:'10px', marginTop:'10px'}}
            model={menuItems}
            pt={{
              root: { className: 'py-0 sm:py-0' },
              menu: { className: 'py-0' },
              menuitem: { className: 'py-0' },
              action: { className: 'py-1 sm:py-1' },
              label: { className: 'text-sm' }
            }}
          />
        {currentPage === 'dashboard' && <Dashboard toastRef={toastRef} />}
        {currentPage === 'configuration' && (
          <Configuration 
            toastRef={toastRef} 
            isGlobalConfigSaved={isGlobalConfigSaved}
            setIsGlobalConfigSaved={setIsGlobalConfigSaved}
          />
        )}
      </div>
    </div>
  );
}

function App() {
  return (
    <PrimeReactProvider>
      <Router>
        <ImportProvider>
          <Routes>
            <Route path="/" element={<AppContent />} />
            <Route path="/importacoes-automatizadas" element={
              <div className="min-h-screen bg-gray-50 flex flex-col">
                <Toast ref={React.createRef()} />
                <AutomatedImports toastRef={React.createRef()} />
              </div>
            } />
            <Route path="/upload/:token?" element={<MissingLayoutsUploadPage />} />
          </Routes>
        </ImportProvider>
      </Router>
    </PrimeReactProvider>
  );
}

export default App;

